import { useMemo } from 'react'

import {
  CreateFormDocument,
  FormPreviewResponsesDocument,
  FormResponseFragment,
  FormsElementParamsInput,
  FormsElementTypeEnum,
  FormsFormStatusEnum,
  FormsTypeEnum,
  LoadFormDocument,
  LoadFormTranslatedDocument,
  UpdateFormDocument,
  UpdateFormMutationVariables,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { DateElement } from 'modules/forms/lib/elements/DateElement'
import { DropdownElement } from 'modules/forms/lib/elements/DropdownElement'
import { LongTextElement } from 'modules/forms/lib/elements/LongTextElement'
import { MultipleChoiceElement } from 'modules/forms/lib/elements/MultipleChoiceElement'
import { NumberElement } from 'modules/forms/lib/elements/NumberElement'
import { RatingElement } from 'modules/forms/lib/elements/RatingElement'
import { ScoreElement } from 'modules/forms/lib/elements/ScoreElement'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { SingleChoiceElement } from 'modules/forms/lib/elements/SingleChoiceElement'
import { TextElement } from 'modules/forms/lib/elements/TextElement'
import { UrlElement } from 'modules/forms/lib/elements/UrlElement'
import { ElementData, ElementOption } from 'modules/forms/lib/types'

type ElementConstructor = new (data: ElementData) => Element

// This definition is used to know the class depending
// on the element type
const elementTypeToClass: { [key in FormsElementTypeEnum]: ElementConstructor } = {
  text: TextElement,
  longtext: LongTextElement,
  select: SingleChoiceElement,
  multiselect: MultipleChoiceElement,
  number: NumberElement,
  section: SectionElement,
  rating: RatingElement,
  score: ScoreElement,
  url: UrlElement,
  dropdownsingle: DropdownElement,
  date: DateElement,
}

// The object shape for 'FormsElement'
type ElementInput = ElementData & {
  params: FormsElementParamsInput & { __typename?: string }
  type: FormsElementTypeEnum
  updatedAt?: string | undefined
  createdAt?: string | undefined
}

// The object shape for 'FormsForm'
type FormInput = {
  id: string
  anonymous: boolean
  description?: string
  elements: ElementInput[]
  formType: FormsTypeEnum
  resourceId?: string
  resourceType: string
  status: FormsFormStatusEnum
  title: string
  updatedAt?: string | undefined
  createdAt?: string | undefined
  authorFullName?: string
  authorAvatarUrl?: string
}

export const formFromData = (formData: FormInput): Form => {
  const form = new Form(
    formData.id,
    formData.title,
    formData.description ?? '',
    formData.resourceType,
    formData.resourceId ?? null,
    formData.formType,
    formData.status,
    formData.anonymous,
    [],
    {}
  )
  form.authorFullName = formData.authorFullName ?? ''
  form.authorAvatarUrl = formData.authorAvatarUrl ?? ''
  form.updatedAt = formData.updatedAt ?? undefined
  form.createdAt = formData.createdAt ?? undefined

  formData.elements.sort((a: ElementInput, b: ElementInput) => {
    // order by section asc
    if (a.sectionId !== b.sectionId) {
      return Number(a.sectionId) - Number(b.sectionId)
    }
    //if same section, order by position
    if (a.position !== b.position) {
      return (a.position || 0) - (b.position || 0)
    }
    //if same position, order by id
    return Number(a.id) - Number(b.id)
  })

  let section: SectionElement | undefined
  formData.elements.forEach((elementData) => {
    if (elementData.type in elementTypeToClass) {
      const newElement = elementFromData(elementData)

      if (newElement.sectionId) {
        if (!section || section.id != newElement.sectionId) {
          section = form.elements
            .filter((element) => element instanceof SectionElement)
            .find((element) => element.id == newElement.sectionId)
        }
        if (section) {
          section.addElement(newElement)
        } else {
          throw new Error(`section: ${elementData.sectionId} not found`)
        }
      } else {
        form.addElement(newElement)
      }
    } else {
      throw new Error(`Unknown element type: ${elementData.type}`)
    }
  })
  return form
}

const formAnsweredFromData = ({
  formData,
  answers,
}: {
  formData: FormInput
  answers: FormResponseFragment[]
}) => {
  const form = formFromData(formData)
  answers.forEach((answer: FormResponseFragment) => {
    const element = form.getElement(answer.elementId)
    if (!element) {
      throw new Error(`Element ${answer.elementId} not found for answer ${answer.id}`)
    }
    element.answer = {
      answer: answer.answer ?? '',
      comment: answer.comment ?? '',
      options: answer.elementOptionIds ?? [],
      isCorrect: answer.isCorrect ?? undefined,
    }
  })

  return form
}

/**
 * Creates a form element with the data coming from backend or other place
 *
 * @param elementData
 */
export const elementFromData = (elementData: ElementInput): Element => {
  const elementClass = elementTypeToClass[elementData.type]
  if (!elementClass) {
    throw new Error(`Unknown element type: ${elementData.type}`)
  }
  // __typename is added by graphql automatically on read
  // usually we select each element one by one but here we take all to minimize
  // the changes needed to update parmas later on
  const { __typename, ...paramsWithoutTypename } = elementData.params

  const newElement = new elementClass({
    id: elementData.id,
    label: elementData.label,
    description: elementData.description,
    position: elementData.position,
    mandatory: elementData.mandatory,
    params: paramsWithoutTypename,
    options: elementData.options || [],
    sectionId: elementData.sectionId,
    answer: elementData.answer,
    hasCorrectAnswer: elementData.hasCorrectAnswer,
  })
  newElement.updatedAt = elementData.updatedAt ?? undefined
  newElement.createdAt = elementData.createdAt ?? undefined
  if (elementData.options && elementData.options.length > 0) {
    // replace element options (score element add default options in constructor)
    newElement.options = elementData.options.map((option) => {
      return {
        value: option.value,
        label: option.label,
        id: option.id,
        isCorrect: option.isCorrect,
      }
    })
  }
  return newElement
}

export const getDefaultOptions = (
  type: FormsElementTypeEnum,
  hasCorrectAnswer?: boolean
): ElementOption[] => {
  const scale: ElementOption[] = [
    { label: i18n.t('forms.default_rating_scale.very_low'), value: '1', id: '', isCorrect: false },
    { label: i18n.t('forms.default_rating_scale.low'), value: '2', id: '', isCorrect: false },
    { label: i18n.t('forms.default_rating_scale.average'), value: '3', id: '', isCorrect: false },
    { label: i18n.t('forms.default_rating_scale.high'), value: '4', id: '', isCorrect: false },
    { label: i18n.t('forms.default_rating_scale.very_high'), value: '5', id: '', isCorrect: false },
  ]
  const defaultOptions = (elementType: FormsElementTypeEnum): ElementOption[] => {
    switch (elementType) {
      case 'multiselect':
      case 'select':
      case 'dropdownsingle':
        return [
          {
            label: i18n.t('forms.editor.options.option_placeholder', { position: 1 }),
            value: '',
            id: '',
            isCorrect: hasCorrectAnswer ?? false,
          },
          {
            label: i18n.t('forms.editor.options.option_placeholder', { position: 2 }),
            value: '',
            id: '',
            isCorrect: false,
          },
        ]
      case 'rating':
      case 'score':
        return scale
      default:
        return []
    }
  }

  return defaultOptions(type)
}

export const useCreateForm = () => {
  const [createForm, { loading }] = useMutation(CreateFormDocument, {
    extractorFn: (data) => data.formsMutations.createFormsForm,
  })

  return {
    creatingForm: loading,
    createForm: async (form: Form, companyId: number, accessId: number) => {
      const result = await createForm({
        variables: {
          anonymous: form.anonymous,
          authorId: accessId,
          companyId: companyId,
          description: form.description || '',
          elements: form.elements.map((e) => e.toBackend()),
          formType: form.formType,
          status: form.status,
          //TODOUntil we can create forms without a resourceId, we need to pass an empty string
          resourceId: form.resourceId ?? '',
          resourceType: form.resourceType,
          title: form.title,
        },
      })
      return handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('forms.form_create_error'),
        })
      })
    },
  }
}

export const useUpdateForm = () => {
  const [updateForm, { loading }] = useMutation(UpdateFormDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })

  return {
    updatingForm: loading,
    updateForm: async (form: Form) => {
      const result = await updateForm({
        variables: {
          id: form.id,
          anonymous: form.anonymous,
          description: form.description || '',
          elements: form.elements.map((e) => e.toBackend()),
          title: form.title,
        },
      })
      return handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('forms.form_update_error'),
        })
      })
    },
  }
}

export const useUpdateNewForm = () => {
  const [updateForm, { loading }] = useMutation(UpdateFormDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })

  return {
    updatingForm: loading,
    updateForm: async (form: UpdateFormMutationVariables) => {
      const result = await updateForm({
        variables: {
          id: form.id,
          anonymous: form.anonymous,
          description: form.description || '',
          elements: form.elements || [],
          title: form.title,
        },
      })
      return handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('forms.form_update_error'),
        })
      })
    },
  }
}

export const useLoadResponses = ({
  membershipId,
  resourceType,
  materializedFormResourceType,
  formId,
  respondentId,
}: {
  membershipId: string
  resourceType: string
  materializedFormResourceType: string
  formId: string
  respondentId?: string
}) => {
  const { data: backendData, loading: loadingForm } = useQuery(FormPreviewResponsesDocument, {
    variables: {
      membershipIds: [membershipId],
      resourceType,
      materializedFormResourceType,
      formId,
      respondentId,
    },
    // skip: !membershipId || !resourceType || !materialized
  })

  const answers = backendData?.forms?.membershipsConnection?.edges[0]?.node?.answers ?? []
  const formData = backendData?.forms?.membershipsConnection?.edges[0]?.node?.materializedForm?.form

  const form =
    !loadingForm && formData
      ? formAnsweredFromData({
          formData: {
            ...formData,
            resourceId: formData.resourceId ?? undefined,
            description: formData.description ?? '',
            elements: formData.elements.map((el) => ({
              ...el,
              description: el.description ?? '',
              updatedAt: el.updatedAt ?? undefined,
              createdAt: el.createdAt ?? undefined,
            })),
          },
          answers,
        })
      : undefined

  return { form, loadingForm }
}

export const useLoadForm = ({
  formId,
  resourceType,
  locale,
}: {
  formId: string | undefined
  resourceType: string | undefined
  locale?: string | undefined
}): { form: Form | undefined; loadingForm: boolean } => {
  //load form from backend if we don't have 'form' parameter
  const queryDocument = locale !== undefined ? LoadFormTranslatedDocument : LoadFormDocument
  //TODO Check if resourceId is needed in the backend to validate read permission
  const { data: backendData, loading: loadingForm } = useQuery(queryDocument, {
    variables: {
      id: formId ?? '',
      resourceType: resourceType ?? '',
      locale: locale ?? undefined,
    },
    skip: !formId && !resourceType,
  })
  const formData = backendData?.forms.form

  const form = useMemo(() => {
    return !loadingForm && formData
      ? formFromData({
          ...formData,
          resourceId: formData.resourceId ?? undefined,
          description: formData.description ?? '',
          authorFullName: formData.author?.employee?.fullName ?? '',
          authorAvatarUrl: formData.author?.employee?.avatar?.url ?? '',
          elements: formData.elements.map((el) => ({
            ...el,
            description: el.description ?? '',
          })),
        })
      : undefined
  }, [loadingForm, formData])

  return { form, loadingForm }
}

// If you update QuestionnaireSchema or ElementSchema you will need to update the schema in the Mastra project as well.
// factorial-agent/src/mastra/agents/talent/surveys/types.ts
const OptionSchema = z.object({
  id: z.string().optional().describe('Option ID (for existing options)'),
  value: z.string().describe('Option value'),
  label: z.string().describe('Option label text'),
  isCorrect: z.boolean().describe('Whether the option is correct').default(false),
})

const ElementSchema = z.object({
  id: z.string().optional().describe('Element ID (for existing elements)'),
  sectionId: z.string().optional().describe('Section ID (for existing sections)'),
  label: z.string().describe('Element label/title'),
  description: z.string().describe('Element description').default(''),
  type: z
    .enum([
      'text',
      'longtext',
      'select',
      'multiselect',
      'number',
      'rating',
      'score',
      'section',
      'url',
      'date',
      'dropdownsingle',
    ])
    .describe(
      'Element type: text, longtext, select, multiselect, number, rating, score, section, url, date, dropdownsingle'
    ),
  position: z.number().int().describe('Position/order of the element in the survey'),
  mandatory: z.boolean().describe('Whether the element is required to answer').default(false),
  params: z
    .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .describe('Additional parameters for the element (e.g., min/max for numbers)')
    .default({}),
  options: z
    .array(OptionSchema)
    .nullable()
    .describe('Available options for select/multiselect elements')
    .default(null),
  hasCorrectAnswer: z.boolean().optional().describe('Whether the element has correct answers'),
})

const SectionSchema = z.object({
  id: z.string().optional().describe('Section ID (for existing sections)'),
  sectionId: z.string().optional().describe('Section ID (for existing sections)'),
  label: z.string().describe('Section label/title'),
  description: z.string().describe('Section description').default(''),
  type: z.literal('section').describe('Section type'),
  position: z.number().int().describe('Position/order of the section in the survey'),
  mandatory: z.boolean().describe('Whether the section is required to answer').default(false),
  elements: z.array(ElementSchema).describe('List of section elements'),
  params: z
    .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .describe('Additional parameters for the element (e.g., min/max for numbers)')
    .default({}),
  options: z
    .array(OptionSchema)
    .nullable()
    .describe('Available options for select/multiselect elements')
    .default(null),
  hasCorrectAnswer: z.boolean().optional().describe('Whether the element has correct answers'),
})

const QuestionnaireSchema = z.object({
  title: z.string().describe('Survey title'),
  description: z.string().describe('Survey description'),
  elements: z.array(z.union([ElementSchema, SectionSchema])).describe('List of survey elements'),
})

export type QuestionnaireType = z.infer<typeof QuestionnaireSchema>

export const buildStateElements = (elements: QuestionnaireType['elements']): Element[] => {
  return elements.map((element) => {
    // Convert schema type to FormsElementTypeEnum (schema types are a subset)
    const elementType: FormsElementTypeEnum = (() => {
      switch (element.type) {
        case 'text':
          return 'text'
        case 'longtext':
          return 'longtext'
        case 'select':
          return 'select'
        case 'multiselect':
          return 'multiselect'
        case 'rating':
          return 'rating'
        case 'score':
          return 'score'
        case 'section':
          return 'section'
        case 'url':
          return 'url'
        case 'number':
          return 'number'
        default:
          return 'text'
      }
    })()

    return elementFromData({
      id: '', // Will be generated by backend
      label: element.label,
      description: element.description || '',
      type: elementType,
      position: element.position,
      mandatory: element.mandatory ?? false,
      params: element.params || {},
      options:
        element.options?.map((option) => ({
          id: '', // Will be generated by backend
          label: option.label,
          value: option.value,
          isCorrect: false,
        })) || [],
      sectionId: undefined,
      answer: undefined,
      hasCorrectAnswer: false,
    })
  })
}
